// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { getConfig } from '../config';
import { captureRegisterAttribution, getFunnelPayload } from './funnel';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const SCRIPT_ID = 'polaris-ga4-script';

function ensureGoogleAnalytics(measurementId: string): void {
  window.dataLayer = window.dataLayer ?? [];
  window.gtag =
    window.gtag ??
    function gtag(): void {
      // gtag.js expects the standard Arguments-object queue entries, not rest-parameter arrays.
      // biome-ignore lint/complexity/noArguments: Required to match Google's documented gtag queue contract.
      window.dataLayer?.push(arguments);
    };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });

  if (document.getElementById(SCRIPT_ID)) {
    return;
  }
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

export function FunnelAnalytics(): null {
  const location = useLocation();
  const measurementId = getConfig().patientAppGa4MeasurementId;
  const isFunnelRoute = location.pathname === '/register' || location.pathname.startsWith('/onboarding/');

  useEffect(() => {
    if (!measurementId || !isFunnelRoute) {
      return;
    }
    const url = new URL(window.location.href);
    if (location.pathname === '/register') {
      captureRegisterAttribution(url);
    }
    ensureGoogleAnalytics(measurementId);
    window.gtag?.('event', 'page_view', getFunnelPayload(url));
  }, [isFunnelRoute, location, measurementId]);

  return null;
}
